/**
 * 서비스 점검 화면(Maintenance) 판단 기준 및 대상 API 설정
 *
 * 요구사항:
 * 1. 핵심 API(로그인, 회원조회, 메인홈 상품/카테고리)에서 502~504 / timeout / 네트워크 실패 발생 시 Health Check 트리거
 * 2. 부가 API(배너, 팝업, 프로모션 등) 및 개별 상세 페이지 단독 오류는 제외
 * 3. 헬스체크 비정상(200 이외 또는 timeout/무응답) 시 전체 점검 화면 노출
 */

export const MAINTENANCE_CONFIG = {
  // 1. 점검 판단 대상 핵심 API 목록 (prefix 또는 정확한 엔드포인트)
  CORE_API_PATTERNS: [
    '/api/signin/login', // 로그인
    '/api/authentication/issue-token', // 토큰 재발급 / 세션 유지
    '/api/user/me', // 회원 기본 정보 조회
    '/api/category', // 카테고리 목록
    '/api/home', // 메인홈 상품 목록 등
  ] as const,

  // 2. 명시적 제외 API 목록 (오류가 발생해도 점검 화면으로 판단하지 않음)
  EXCLUDED_API_PATTERNS: [
    '/api/banner', // 배너
    '/api/popup', // 팝업
    '/api/promotion', // 프로모션
    '/api/search/popular', // 인기 검색어
    '/api/notification', // 알림
  ] as const,

  // 3. 서버 장애로 간주할 HTTP 에러 상태 코드
  TRIGGER_STATUS_CODES: [502, 503, 504] as const,

  // 4. 타임아웃 설정 (ms)
  API_TIMEOUT_MS: 1000 * 10, // 일반 핵심 API 타임아웃 (10초)
  HEALTH_CHECK_TIMEOUT_MS: 1000 * 3, // 헬스체크 타임아웃 (3초 - 빠른 판단용)

  // 5. 헬스체크 엔드포인트 (토큰 미포함 공개 엔드포인트)
  HEALTH_CHECK_PATH: '/health/check',

  // 6. [다시 시도] 연타 방지 쿨타임 (ms) - 클라이언트 DoS 방어
  RETRY_COOLDOWN_MS: 1000 * 2,
} as const;

/**
 * 주어진 API 엔드포인트가 점검 판단 대상 핵심 API인지 여부 확인
 */
export const isCoreApi = (endpoint: string): boolean => {
  if (isExcludedApi(endpoint)) {
    return false;
  }
  return MAINTENANCE_CONFIG.CORE_API_PATTERNS.some(pattern => endpoint.includes(pattern));
};

/**
 * 주어진 API 엔드포인트가 점검 판단 제외 대상 부가 API인지 여부 확인
 */
export const isExcludedApi = (endpoint: string): boolean => {
  return MAINTENANCE_CONFIG.EXCLUDED_API_PATTERNS.some(pattern => endpoint.includes(pattern));
};
